import * as Sentry from "@sentry/react";

export function initSentry() {
  console.log('[Sentry] Initializing with DSN:', import.meta.env.VITE_SENTRY_DSN || "https://47f711874e85cab87734689d37302836@o4509592112791552.ingest.us.sentry.io/4509592114102272");
  
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://47f711874e85cab87734689d37302836@o4509592112791552.ingest.us.sentry.io/4509592114102272",
    
    // Environment configuration
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    
    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION,
    
    // User privacy settings
    sendDefaultPii: true, // Send default PII data to Sentry as recommended
    
    // Session replay - using correct property names
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    
    // Filtering
    ignoreErrors: [
      // Browser errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      
      // User canceled actions
      'AbortError',
      
      // Extensions
      'chrome-extension://',
      'moz-extension://',
    ],
    
    // Data scrubbing
    beforeSend(event) {
      // Filter out development errors
      if (import.meta.env.DEV && event.exception?.values?.[0]?.value?.includes('[HMR]')) {
        return null;
      }
      
      // Add additional context
      if (event.request) {
        event.request.headers = {
          ...event.request.headers,
          'X-App-Version': import.meta.env.VITE_APP_VERSION,
        };
      }
      
      return event;
    },
    
    // Breadcrumb filtering
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }
      
      // Enhance navigation breadcrumbs
      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = {
          ...breadcrumb.data,
          timestamp: new Date().toISOString(),
        };
      }
      
      return breadcrumb;
    },
  });
  
  console.log('[Sentry] Initialization complete');
  
  // Test that Sentry is working
  if (import.meta.env.DEV) {
    console.log('[Sentry] Running in dev mode, Sentry should be active');
  }
}

// Helper to capture exceptions with additional context
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('additional', context);
    }
    Sentry.captureException(error);
  });
}

// Helper for performance monitoring
export function startTransaction(name: string, op: string) {
  return Sentry.startSpan({
    name,
    op,
  }, () => {
    // Span callback - will be executed within the span context
    return Promise.resolve();
  });
}

// Helper to add breadcrumbs
export function addBreadcrumb(
  message: string, 
  category: string, 
  data?: Record<string, any>,
  level: Sentry.SeverityLevel = 'info'
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

// Helper to set user context
export function setUserContext(user: { id: string; email?: string; username?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}

// Helper to set additional context
export function setContext(key: string, context: Record<string, any>) {
  Sentry.setContext(key, context);
}

// Export Sentry for direct use
export { Sentry };